import { formatDate } from '@/_utils/date';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { isTimelineItemAssigned } from '@/components/Packaging/typeGuards';
import { useAppSelector } from '@/redux/hooks';
import { selectPackagingTimelineFrameRate } from '@/redux/selectors/packagingSelectors';
import { selectPresentTimelineInOrder } from '@/redux/selectors/packagingTimelineSelectors';
import { TimelineItem } from '@/redux/slices/packagingSlice.types';
import styled from '@emotion/styled';
import { Container, Grid, Typography } from '@mui/material';
import { getOptionColor, getSMPTETimecode } from '../utils';

const ComponentList = () => {
  const sortedTimeline = useAppSelector(selectPresentTimelineInOrder);
  const frameRate = useAppSelector(selectPackagingTimelineFrameRate);

  const columns = [
    { label: 'Type', col: 2 },
    { label: 'Edit Title Name', col: 2 },
    { label: 'Is Screened', col: 1 },
    { label: 'External Id', col: 2 },
    { label: 'Rights Window', col: 2 },
    { label: 'Usage Window', col: 2 },
    { label: 'Duration', col: 1 }
  ];

  return (
    <Wrapper>
      <WrapperGrid container rowGap={0.5}>
        {/* Header Row */}
        <HeaderRow item container>
          {columns.map((column) => (
            <Grid item xs={column.col} key={column.label}>
              {column.label}
            </Grid>
          ))}
        </HeaderRow>

        {sortedTimeline.map((item: TimelineItem) => {
          const color = getOptionColor(item.kind);
          const start = item.start;
          const end = item.end;
          const duration = end - start;
          const rightsWindowStart = undefined;
          const rightsWindowEnd = undefined;
          const usageWindowStart = isTimelineItemAssigned(item) ? item.source.use_window_start : undefined;
          const usageWindowEnd = isTimelineItemAssigned(item) ? item.source.use_window_end : undefined;

          return (
            <ComponentRow item container tab={color} key={item.label}>
              <Grid item xs={columns[0].col}>
                <PolarisIcon icon="cards" height={20} width={20} />
                <KindLabel>{item.label}</KindLabel>
              </Grid>
              <Grid item xs={columns[1].col}>
                <EllipsisCell>{isTimelineItemAssigned(item) ? (item.source?.titleName ?? '-') : '-'}</EllipsisCell>
              </Grid>
              <Grid item xs={columns[2].col}>
                <EllipsisCell>
                  {isTimelineItemAssigned(item) && item.source.is_screened ? <PolarisIcon icon="complete" width="18" /> : '-'}
                </EllipsisCell>
              </Grid>
              <Grid item xs={columns[3].col}>
                <span>{(item as TimelineItem & { source: { external_id?: string } }).source?.external_id || '-'}</span>
              </Grid>
              <Grid item xs={columns[4].col}>
                {rightsWindowStart && rightsWindowEnd ? (
                  <>
                    <span>{formatDate(rightsWindowStart)}</span>
                    <Typography>-</Typography>
                    <span>{formatDate(rightsWindowEnd)}</span>
                  </>
                ) : (
                  <span>-</span>
                )}
              </Grid>
              <Grid item xs={columns[5].col}>
                {usageWindowStart && usageWindowEnd ? (
                  <>
                    <span>{usageWindowStart}</span>
                    <Typography>-</Typography>
                    <span>{usageWindowEnd}</span>
                  </>
                ) : (
                  <span>-</span>
                )}
              </Grid>

              <Grid item xs={columns[6].col} textAlign={'right'}>
                <Timestamp>
                  <div>{getSMPTETimecode(duration, frameRate)}</div>
                  <div>
                    {getSMPTETimecode(start, frameRate)} - {getSMPTETimecode(end, frameRate)}
                  </div>
                </Timestamp>
              </Grid>
            </ComponentRow>
          );
        })}
      </WrapperGrid>
    </Wrapper>
  );
};

export default ComponentList;

const Wrapper = styled(Container)`
  height: 100%;
  padding: ${({ theme }) => theme.spacing(3, 0, 7)};
`;

const WrapperGrid = styled(Grid)`
  > .MuiGrid-item {
    padding: ${({ theme }) => theme.spacing(1, 2)};
  }
`;

const HeaderRow = styled(Grid)`
  background: ${({ theme }) => theme.palette.background.paper};
  align-items: center;
  margin: 0px;
  border-top-left-radius: ${({ theme }) => theme.shape.borderRadius}px;
  border-top-right-radius: ${({ theme }) => theme.shape.borderRadius}px;

  .MuiGrid-item {
    font-weight: ${({ theme }) => theme.typography.fontWeightBold};
    font-size: 1.1rem;
  }

  .MuiGrid-item:first-of-type {
    padding-left: ${({ theme }) => theme.spacing(1.25)};
  }

  .MuiGrid-item:last-of-type {
    text-align: right;
  }
`;

const ComponentRow = styled(Grid)<{ tab: string }>`
  margin: 0px;
  height: 55px;
  background: ${({ theme }) => theme.palette.primary.main + '3a'};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  position: relative;
  overflow: hidden;
  align-items: center;
  justify-content: space-between;

  ::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${({ theme }) => theme.spacing(1.25)};
    background: ${({ tab }) => tab};
  }

  .MuiGrid-item {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    column-gap: ${({ theme }) => theme.spacing(0.6)};
  }

  .MuiGrid-item:first-of-type {
    padding-left: ${({ theme }) => theme.spacing(1.25)};
  }

  .MuiGrid-item:last-of-type {
    align-items: flex-end;
  }
`;

const KindLabel = styled(Typography)`
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  font-size: 1.1rem;
`;

const Timestamp = styled.div`
  font-size: 0.9rem;
  width: 100%;

  div:first-of-type {
    font-weight: ${({ theme }) => theme.typography.fontWeightBold};
    font-size: 1rem;
  }

  div:last-of-type {
    font-size: 0.8rem;
    color: ${({ theme }) => theme.palette.text.secondary};
  }
`;

const EllipsisCell = styled.div`
  max-width: 90%;
  width: fit-content;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
